import { useCallback, useRef, useState, useEffect } from 'react';
import { useStore } from '@/enthea/lib/store';
import { Engine } from '@/enthea/lib/engine';
import { MODES, PALETTES } from '@/enthea/lib/constants';

/* ── tiny helpers ──────────────────────────────────────────── */

function ClampSlider({
  label,
  value,
  min = 0,
  max = 1,
  step = 0.01,
  onChange,
  accent = false,
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange: (v: number) => void;
  accent?: boolean;
}) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="flex flex-col gap-1 min-w-0 flex-1">
      <div className="flex justify-between items-baseline px-0.5">
        <span className="text-[10px] tracking-[0.12em] uppercase text-white/50 font-medium">
          {label}
        </span>
        <span className="text-[10px] text-white/30 tabular-nums font-mono ml-1">
          {step >= 1 ? Math.round(value) : value.toFixed(2)}
        </span>
      </div>
      <div className="relative h-7 sm:h-5 flex items-center group">
        <div className="absolute inset-x-0 h-[3px] sm:h-[2px] rounded-full bg-white/[0.06]" />
        <div
          className="absolute left-0 h-[3px] sm:h-[2px] rounded-full transition-all duration-75"
          style={{
            width: `${pct}%`,
            background: accent
              ? 'linear-gradient(90deg, rgba(168,85,247,0.7), rgba(236,72,153,0.9))'
              : 'linear-gradient(90deg, rgba(255,255,255,0.15), rgba(255,255,255,0.35))',
          }}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
        />
        <div
          className="absolute w-4 h-4 sm:w-3 sm:h-3 rounded-full border border-white/20 shadow-lg transition-all duration-75 pointer-events-none group-hover:scale-125 group-hover:border-white/40"
          style={{
            left: `calc(${pct}% - 8px)`,
            background: accent
              ? 'radial-gradient(circle, rgba(236,72,153,0.9), rgba(168,85,247,0.7))'
              : 'radial-gradient(circle, rgba(255,255,255,0.6), rgba(255,255,255,0.2))',
            boxShadow: accent
              ? '0 0 10px rgba(168,85,247,0.5)'
              : '0 0 6px rgba(255,255,255,0.15)',
          }}
        />
      </div>
    </div>
  );
}

function PillButton({
  active,
  onClick,
  children,
  title,
  danger,
  small,
}: {
  active?: boolean;
  onClick: (e?: React.MouseEvent) => void;
  children: React.ReactNode;
  title?: string;
  danger?: boolean;
  small?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`
        ${small ? 'px-2 py-1.5' : 'px-2.5 py-1.5 sm:px-2.5 sm:py-1.5'} rounded-full text-[10px] tracking-wider uppercase font-medium
        border transition-all duration-300 backdrop-blur-sm whitespace-nowrap
        ${
          active
            ? danger
              ? 'bg-red-500/20 border-red-400/40 text-red-300 shadow-[0_0_12px_rgba(239,68,68,0.2)]'
              : 'bg-white/[0.12] border-white/25 text-white/90 shadow-[0_0_12px_rgba(255,255,255,0.06)]'
            : 'bg-white/[0.03] border-white/[0.08] text-white/40 hover:text-white/60 hover:border-white/15'
        }
      `}
    >
      {children}
    </button>
  );
}

/* ── dropdown wrapper (mobile-aware) ───────────────────────── */

function DropdownPanel({
  show,
  children,
  className = '',
}: {
  show: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  if (!show) return null;
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className={`
        fixed inset-x-2 bottom-[calc(env(safe-area-inset-bottom,0px)+60px)]
        sm:absolute sm:inset-auto sm:bottom-full sm:mb-2 sm:right-0
        max-h-[55vh] overflow-y-auto rounded-xl
        bg-black/90 sm:bg-black/85 backdrop-blur-2xl
        border border-white/10 shadow-2xl
        scrollbar-thin ${className}
      `}
    >
      {children}
    </div>
  );
}

/* ── main component ────────────────────────────────────────── */

export function EntheaControls() {
  const st = useStore();
  const presets = useStore((s) => s.presets);
  const savePreset = useStore((s) => s.savePreset);
  const loadPreset = useStore((s) => s.loadPreset);
  const deletePreset = useStore((s) => s.deletePreset);
  const exportPresets = useStore((s) => s.exportPresets);
  const importPresets = useStore((s) => s.importPresets);
  const fileRef = useRef<HTMLInputElement>(null);
  const presetImportRef = useRef<HTMLInputElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [trackName, setTrackName] = useState('');
  const [showModes, setShowModes] = useState(false);
  const [showPalettes, setShowPalettes] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [presetName, setPresetName] = useState('');

  const up = useCallback(
    (p: Record<string, unknown>) => useStore.getState().updateParams(p),
    [],
  );

  /* close dropdowns on outside click
     Delay registration by one frame so the opening click event finishes
     bubbling before we start listening — otherwise the same click that
     opens the dropdown immediately closes it. */
  useEffect(() => {
    if (!showModes && !showPalettes && !showPresets) return;
    const close = () => { setShowModes(false); setShowPalettes(false); setShowPresets(false); };
    const timer = setTimeout(() => window.addEventListener('click', close), 0);
    return () => { clearTimeout(timer); window.removeEventListener('click', close); };
  }, [showModes, showPalettes, showPresets]);

  const handleFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setTrackName(f.name);
    Engine.loadTrack(f);
  }, []);

  const toggleMic = useCallback(async () => {
    if (st.audioOn && st.audioSource === 'mic') {
      Engine.stopAudio();
      setTrackName('');
    } else {
      setTrackName('');
      await Engine.initAudio('mic');
    }
  }, [st.audioOn, st.audioSource]);

  const uploadTrack = useCallback(() => fileRef.current?.click(), []);

  const togglePlay = useCallback(() => {
    Engine.toggleTrackPlay();
  }, []);

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="audio/*"
        className="hidden"
        onChange={handleFile}
      />

      {/* ── bottom bar ── */}
      <div className="fixed bottom-0 inset-x-0 z-50 pointer-events-none">
        {/* gradient fade */}
        <div className="h-8 sm:h-12 bg-gradient-to-t from-black/80 via-black/30 to-transparent pointer-events-none" />

        <div
          className="bg-black/60 backdrop-blur-xl border-t border-white/[0.06] pointer-events-auto"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          {/* ═══ MOBILE LAYOUT ═══ */}
          <div className="sm:hidden">
            {/* Row 1: audio buttons + key actions */}
            <div className="flex items-center gap-1.5 px-2.5 py-2">
              {/* Audio trio */}
              <button
                onClick={toggleMic}
                className={`w-9 h-9 rounded-full flex items-center justify-center border transition-all duration-300 ${
                  st.audioOn && st.audioSource === 'mic'
                    ? 'bg-purple-500/25 border-purple-400/40 text-purple-300'
                    : 'bg-white/[0.04] border-white/10 text-white/40'
                }`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" x2="12" y1="19" y2="22"/>
                </svg>
              </button>

              <button
                onClick={uploadTrack}
                className={`w-9 h-9 rounded-full flex items-center justify-center border transition-all duration-300 ${
                  st.audioOn && st.audioSource === 'file'
                    ? 'bg-pink-500/25 border-pink-400/40 text-pink-300'
                    : 'bg-white/[0.04] border-white/10 text-white/40'
                }`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" x2="12" y1="3" y2="15"/>
                </svg>
              </button>

              <button
                onClick={togglePlay}
                disabled={!(st.audioOn && st.audioSource === 'file')}
                className={`w-9 h-9 rounded-full flex items-center justify-center border transition-all duration-300 ${
                  !(st.audioOn && st.audioSource === 'file')
                    ? 'bg-white/[0.02] border-white/[0.06] text-white/25 cursor-not-allowed'
                    : st.trackPlaying
                    ? 'bg-emerald-500/25 border-emerald-400/40 text-emerald-300'
                    : 'bg-white/[0.04] border-white/10 text-white/40'
                }`}
              >
                {st.audioOn && st.audioSource === 'file' && st.trackPlaying ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                    <rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" />
                  </svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                    <path d="M7 4.5v15a1 1 0 0 0 1.55.83l12-7.5a1 1 0 0 0 0-1.66l-12-7.5A1 1 0 0 0 7 4.5Z" />
                  </svg>
                )}
              </button>

              {/* Track name */}
              {trackName && (
                <span className="text-[9px] text-white/30 font-mono truncate flex-1 min-w-0">
                  {trackName}
                </span>
              )}

              {/* Spacer */}
              <div className="flex-1" />

              {/* Dose — the most-used slider, always visible */}
              <div className="w-24">
                <ClampSlider
                  label="Dose"
                  value={st.dose}
                  onChange={(v) => up({ dose: v })}
                  accent
                />
              </div>

              {/* Mode pill */}
              <PillButton active onClick={(e) => { e?.stopPropagation(); setShowModes(!showModes); }}>
                {MODES[st.mode]?.name?.split(' ')[0] || 'MODE'}
              </PillButton>

              {/* Expand */}
              <PillButton active={expanded} onClick={() => setExpanded(!expanded)}>
                {expanded ? '✕' : '⋯'}
              </PillButton>
            </div>

            {/* Row 2: Speed + Trail (compact, always visible on mobile) */}
            <div className="flex items-center gap-2 px-2.5 pb-1.5">
              <ClampSlider label="Speed" value={st.speed} min={0.1} max={3} step={0.05} onChange={(v) => up({ speed: v })} />
              <ClampSlider label="Trail" value={st.trail} onChange={(v) => up({ trail: v })} />
            </div>

            {/* Audio stats */}
            {st.audioOn && (
              <div className="flex items-center gap-2.5 px-2.5 pb-1.5 text-[9px] text-white/30 font-mono">
                <span>BPM <span className="text-white/55">{Engine.AUDIO?.a?.bpm || '—'}</span></span>
                <span>Key <span className="text-white/55">
                  {['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'][Engine.AUDIO?.detectedKey || 0]}
                  {Engine.AUDIO?.isMinor ? 'm' : ''}
                </span></span>
              </div>
            )}
          </div>

          {/* ═══ DESKTOP LAYOUT ═══ */}
          <div className="hidden sm:flex items-center gap-2 px-3 py-2">
            {/* play / mic / upload trio */}
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                onClick={toggleMic}
                title={st.audioOn && st.audioSource === 'mic' ? 'Stop mic' : 'Start mic'}
                className={`w-8 h-8 rounded-full flex items-center justify-center border transition-all duration-300 ${
                  st.audioOn && st.audioSource === 'mic'
                    ? 'bg-purple-500/25 border-purple-400/40 text-purple-300 shadow-[0_0_14px_rgba(168,85,247,0.3)]'
                    : 'bg-white/[0.04] border-white/10 text-white/40 hover:text-white/70 hover:border-white/20'
                }`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" x2="12" y1="19" y2="22"/>
                </svg>
              </button>
              <button
                onClick={uploadTrack}
                title="Upload track"
                className={`w-8 h-8 rounded-full flex items-center justify-center border transition-all duration-300 ${
                  st.audioOn && st.audioSource === 'file'
                    ? 'bg-pink-500/25 border-pink-400/40 text-pink-300 shadow-[0_0_14px_rgba(236,72,153,0.3)]'
                    : 'bg-white/[0.04] border-white/10 text-white/40 hover:text-white/70 hover:border-white/20'
                }`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" x2="12" y1="3" y2="15"/>
                </svg>
              </button>
              <button
                onClick={togglePlay}
                disabled={!(st.audioOn && st.audioSource === 'file')}
                title={!(st.audioOn && st.audioSource === 'file') ? 'Upload a track first' : st.trackPlaying ? 'Pause' : 'Play'}
                className={`w-8 h-8 rounded-full flex items-center justify-center border transition-all duration-300 ${
                  !(st.audioOn && st.audioSource === 'file')
                    ? 'bg-white/[0.02] border-white/[0.06] text-white/25 cursor-not-allowed'
                    : st.trackPlaying
                    ? 'bg-emerald-500/25 border-emerald-400/40 text-emerald-300 shadow-[0_0_14px_rgba(16,185,129,0.3)]'
                    : 'bg-white/[0.04] border-white/10 text-white/40 hover:text-white/70 hover:border-white/20'
                }`}
              >
                {st.audioOn && st.audioSource === 'file' && st.trackPlaying ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                    <rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" />
                  </svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                    <path d="M7 4.5v15a1 1 0 0 0 1.55.83l12-7.5a1 1 0 0 0 0-1.66l-12-7.5A1 1 0 0 0 7 4.5Z" />
                  </svg>
                )}
              </button>
            </div>

            {trackName && (
              <span className="text-[10px] text-white/30 font-mono truncate max-w-[120px]">{trackName}</span>
            )}

            {/* Sliders */}
            <div className="flex-1 flex items-center gap-3 overflow-x-auto scrollbar-none pl-1">
              <ClampSlider label="Dose" value={st.dose} onChange={(v) => up({ dose: v })} accent />
              <ClampSlider label="Speed" value={st.speed} min={0.1} max={3} step={0.05} onChange={(v) => up({ speed: v })} />
              <ClampSlider label="Complex" value={st.cplx} onChange={(v) => up({ cplx: v })} />
              <ClampSlider label="Trail" value={st.trail} onChange={(v) => up({ trail: v })} />
            </div>

            {/* Mode / Palette / More */}
            <div className="flex items-center gap-1.5 shrink-0">
              <div className="relative">
                <PillButton active onClick={(e) => { e?.stopPropagation(); setShowModes(!showModes); }}>
                  {MODES[st.mode]?.name || 'MODE'}
                </PillButton>
              </div>
              <div className="relative">
                <PillButton active onClick={(e) => { e?.stopPropagation(); setShowPalettes(!showPalettes); }}>
                  {PALETTES[st.palette] || 'PAL'}
                </PillButton>
              </div>
              <PillButton active={expanded} onClick={() => setExpanded(!expanded)}>
                {expanded ? 'Less' : 'More'}
              </PillButton>
            </div>
          </div>

          {/* ═══ EXPANCED SECTION (shared, responsive) ═══ */}
          {expanded && (
            <div className="border-t border-white/[0.04] px-2.5 sm:px-3 py-2.5">
              {/* Sliders grid — 2-col on mobile, flex on desktop */}
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 sm:flex sm:items-center sm:gap-3 sm:flex-wrap">
                <ClampSlider label="Symmetry" value={st.sym} min={2} max={18} step={1} onChange={(v) => up({ sym: v })} />
                <ClampSlider label="Ascension" value={st.asc} onChange={(v) => up({ asc: v })} accent />
                <ClampSlider label="Chaos" value={st.chaos} onChange={(v) => up({ chaos: v })} />
                <ClampSlider label="Reactivity" value={st.reactivity} onChange={(v) => up({ reactivity: v })} />
                <ClampSlider label="Complex" value={st.cplx} onChange={(v) => up({ cplx: v })} />
                <ClampSlider label="Trail" value={st.trail} onChange={(v) => up({ trail: v })} />
              </div>

              {/* Toggle pills — wraps on all sizes */}
              <div className="flex items-center gap-1.5 flex-wrap mt-2.5">
                <PillButton active={st.journey} onClick={() => up({ journey: !st.journey })}>Journey</PillButton>
                <PillButton active={st.cine} onClick={() => up({ cine: !st.cine })}>Cine</PillButton>
                <PillButton active={st.ray} onClick={() => up({ ray: !st.ray })}>3D Ray</PillButton>
                <PillButton active={st.flicker} onClick={() => up({ flicker: !st.flicker })}>Flicker</PillButton>
                <PillButton active={st.scope} onClick={() => up({ scope: !st.scope })}>Scope</PillButton>
                <PillButton active={st.choreo} onClick={() => up({ choreo: !st.choreo })}>Choreo</PillButton>
                <PillButton active={st.planform !== 0} onClick={() => up({ planform: (st.planform + 1) % 4 })}>
                  {['Auto', 'Tunnel', 'Spiral', 'Lattice'][st.planform]}
                </PillButton>

                {/* Palette (mobile only — desktop has it in the top bar) */}
                <div className="relative sm:hidden">
                  <PillButton active onClick={(e) => { e?.stopPropagation(); setShowPalettes(!showPalettes); }}>
                    {PALETTES[st.palette] || 'PAL'}
                  </PillButton>
                </div>

                {/* Stop audio */}
                {st.audioOn && (
                  <PillButton danger onClick={() => { Engine.stopAudio(); setTrackName(''); }}>
                    Stop Audio
                  </PillButton>
                )}

                {/* Presets */}
                <div className="relative">
                  <PillButton active={showPresets} onClick={(e) => { e?.stopPropagation(); setShowPresets(!showPresets); }}>
                    Presets {presets.length > 0 ? `(${presets.length})` : ''}
                  </PillButton>
                </div>
              </div>

              {/* Audio stats */}
              {st.audioOn && (
                <div className="flex items-center gap-3 mt-2 text-[10px] text-white/35 font-mono">
                  <span>BPM <span className="text-white/60">{Engine.AUDIO?.a?.bpm || '—'}</span></span>
                  <span>Key <span className="text-white/60">
                    {['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'][Engine.AUDIO?.detectedKey || 0]}
                    {Engine.AUDIO?.isMinor ? 'm' : ''}
                  </span></span>
                  <span className="hidden sm:inline">Crest <span className="text-white/60">{(Engine.AUDIO?.a?.crest || 0).toFixed(1)}</span></span>
                  <span className="hidden sm:inline">ZCR <span className="text-white/60">{(Engine.AUDIO?.a?.zcr || 0).toFixed(2)}</span></span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ═══ MODE DROPDOWN (rendered outside bar for proper layering) ═══ */}
      {showModes && (
        <>
          {/* mobile: full-screen backdrop */}
          <div className="fixed inset-0 z-[60] bg-black/40 sm:hidden" onClick={() => setShowModes(false)} />
          <DropdownPanel show={showModes} className="z-[61] p-1.5 w-auto sm:w-64">
            {MODES.map((m, i) => (
              <button
                key={m.id}
                onClick={() => { up({ mode: i }); setShowModes(false); }}
                className={`w-full text-left px-3 py-2.5 sm:py-2 rounded-lg text-[11px] transition-all ${
                  i === st.mode
                    ? 'bg-purple-500/20 text-purple-200 border border-purple-400/20'
                    : 'text-white/50 hover:bg-white/[0.06] hover:text-white/80 border border-transparent'
                }`}
              >
                <div className="font-medium tracking-wide">{m.name}</div>
                <div className="text-[9px] text-white/30 mt-0.5">{m.sub}</div>
              </button>
            ))}
          </DropdownPanel>
        </>
      )}

      {/* ═══ PALETTE DROPDOWN ═══ */}
      {showPalettes && (
        <>
          <div className="fixed inset-0 z-[60] bg-black/40 sm:hidden" onClick={() => setShowPalettes(false)} />
          <DropdownPanel show={showPalettes} className="z-[61] p-2 grid grid-cols-2 sm:grid-cols-2 gap-1 w-auto sm:w-auto">
            {PALETTES.map((p, i) => (
              <button
                key={p}
                onClick={() => { up({ palette: i }); setShowPalettes(false); }}
                className={`px-3 py-2 sm:py-1.5 rounded-lg text-[10px] tracking-wider uppercase transition-all text-left ${
                  i === st.palette
                    ? 'bg-purple-500/20 text-purple-200 border border-purple-400/20'
                    : 'text-white/40 hover:bg-white/[0.06] hover:text-white/70 border border-transparent'
                }`}
              >
                {p}
              </button>
            ))}
          </DropdownPanel>
        </>
      )}

      {/* ═══ PRESETS PANEL ═══ */}
      {showPresets && (
        <>
          <div className="fixed inset-0 z-[60] bg-black/40 sm:hidden" onClick={() => setShowPresets(false)} />
          <DropdownPanel show={showPresets} className="z-[61] p-2 w-auto sm:w-72">
            {/* Save current */}
            <div className="flex gap-1.5 mb-2 px-1">
              <input
                type="text"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder="Preset name..."
                className="flex-1 min-w-0 bg-white/[0.06] border border-white/10 rounded-lg px-2.5 py-2 sm:py-1.5 text-[11px] text-white/80 placeholder:text-white/25 outline-none focus:border-purple-400/40"
              />
              <button
                onClick={() => {
                  if (presetName.trim()) { savePreset(presetName.trim()); setPresetName(''); }
                }}
                className="px-3 py-2 sm:py-1.5 rounded-lg text-[10px] tracking-wider uppercase bg-purple-500/20 border border-purple-400/30 text-purple-300 hover:bg-purple-500/30 transition-all shrink-0"
              >
                Save
              </button>
            </div>

            {/* Import/Export */}
            <div className="flex gap-1.5 mb-2 px-1">
              <input ref={presetImportRef} type="file" accept=".json" className="hidden" onChange={async (e) => {
                const f = e.target.files?.[0]; if (!f) return;
                const text = await f.text(); importPresets(text); e.target.value = '';
              }} />
              <button onClick={() => presetImportRef.current?.click()} className="flex-1 px-2 py-1.5 rounded-lg text-[10px] tracking-wider uppercase bg-white/[0.04] border border-white/10 text-white/40 hover:text-white/70 transition-all">
                Import
              </button>
              <button onClick={() => {
                const data = exportPresets();
                const blob = new Blob([data], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = `enthea-presets-${Date.now()}.json`; a.click();
                URL.revokeObjectURL(url);
              }} className="flex-1 px-2 py-1.5 rounded-lg text-[10px] tracking-wider uppercase bg-white/[0.04] border border-white/10 text-white/40 hover:text-white/70 transition-all">
                Export
              </button>
            </div>

            {/* Preset list */}
            {presets.length === 0 ? (
              <div className="text-[10px] text-white/25 text-center py-3">No saved presets</div>
            ) : (
              <div className="space-y-0.5">
                {presets.map((p, i) => (
                  <div key={`${p.name}-${p.timestamp}`} className="flex items-center gap-2 px-2 py-2 sm:py-1.5 rounded-lg hover:bg-white/[0.06] active:bg-white/[0.1] group transition-all">
                    <button
                      onClick={() => { loadPreset(i); setShowPresets(false); }}
                      className="flex-1 text-left text-[11px] text-white/60 hover:text-white/90 truncate min-w-0"
                    >
                      <span className="text-white/80">{p.name}</span>
                      <span className="text-white/25 ml-1.5 text-[9px]">
                        M{p.params.mode} · D{p.params.dose.toFixed(2)}
                      </span>
                    </button>
                    <button
                      onClick={() => deletePreset(i)}
                      className="text-white/20 hover:text-red-400 transition-colors p-1"
                      title="Delete preset"
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </DropdownPanel>
        </>
      )}
    </>
  );
}
